import { useState } from 'react';
import { X, Loader2, AlertCircle } from 'lucide-react';
import { motion, AnimatePresence } from 'framer-motion';
import { useDecryptedMedia } from '@/hooks/useDecryptedMedia';

interface MediaPreviewProps {
  url: string;
  type: 'image' | 'video';
  thumbnailUrl?: string;
  isOwn?: boolean;
  /** Per-file decryption key for Signal-grade encrypted media */
  fileKey?: string | null;
  /** MIME type for decryption */
  fileType?: string;
}

export function MediaPreview({ url, type, thumbnailUrl, fileKey, fileType }: MediaPreviewProps) {
  const [isLightboxOpen, setIsLightboxOpen] = useState(false);

  const mimeType = fileType || (type === 'image' ? 'image/jpeg' : 'video/mp4');
  const { decryptedUrl, isDecrypting, error } = useDecryptedMedia(url, fileKey, mimeType);

  if (isDecrypting) {
    return (
      <div className="flex items-center justify-center w-[200px] h-[140px] rounded-xl bg-muted/30">
        <Loader2 className="h-6 w-6 animate-spin text-muted-foreground/50" />
      </div>
    );
  }

  if (error || !decryptedUrl) {
    return (
      <div className="flex items-center gap-2 px-3 py-2 rounded-xl bg-destructive/10 text-destructive text-xs">
        <AlertCircle className="h-4 w-4 shrink-0" />
        <span>Failed to decrypt media</span>
      </div>
    );
  }

  return (
    <>
      {type === 'image' ? (
        <motion.img
          whileTap={{ scale: 0.97 }}
          src={decryptedUrl}
          alt="Shared image"
          className="max-w-[260px] max-h-[260px] rounded-xl cursor-pointer object-cover"
          onClick={() => setIsLightboxOpen(true)}
          loading="lazy"
        />
      ) : (
        <div className="relative max-w-[260px] rounded-xl overflow-hidden">
          <video src={decryptedUrl} className="w-full rounded-xl" controls preload="metadata" />
        </div>
      )}

      {/* Fullscreen lightbox */}
      <AnimatePresence>
        {isLightboxOpen && (
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            className="fixed inset-0 z-50 bg-foreground/95 flex items-center justify-center"
            onClick={() => setIsLightboxOpen(false)}
          >
            <motion.button
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="absolute top-6 right-6 h-10 w-10 rounded-full bg-background/20 flex items-center justify-center backdrop-blur-sm z-10"
              onClick={() => setIsLightboxOpen(false)}
            >
              <X className="h-5 w-5 text-background" />
            </motion.button>

            <motion.img
              initial={{ scale: 0.85, opacity: 0 }}
              animate={{ scale: 1, opacity: 1 }}
              exit={{ scale: 0.85, opacity: 0 }}
              transition={{ type: 'spring', stiffness: 300, damping: 25 }}
              src={decryptedUrl}
              alt="Full size"
              className="max-w-[92vw] max-h-[92vh] object-contain rounded-xl"
              onClick={(e) => e.stopPropagation()}
            />
          </motion.div>
        )}
      </AnimatePresence>
    </>
  );
}
